import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Wallet, CreditCard, Coins, Sparkles, Images } from "lucide-react";

import { supabase } from "../../supabaseClient";
import CacheService from "../../services/CacheService.js";

const DashboardNavbar = ({
  displayName,
  creditBalance,
  walletBalance = 0,
  roleLabel,
  pageTitle = "Dashboard",
  navbarOptions,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileWalletOpen, setIsMobileWalletOpen] = useState(false);
  const [isMobileLangOpen, setIsMobileLangOpen] = useState(false);

  // State untuk mengontrol visibilitas Balance (hanya untuk Affiliator)
  const [showBalance, setShowBalance] = useState(() => CacheService.get('navbar_show_balance') || false);

  useEffect(() => {
    const checkAffiliateStatus = async () => {
      // 1. Jika saldo > 0, pasti affiliator (atau setidaknya harus bisa lihat saldo)
      if (walletBalance > 0) {
        setShowBalance(true);
        CacheService.set('navbar_show_balance', true, 60);
        return;
      }

      // 2. Jika saldo 0, cek apakah sudah generate voucher (status affiliator)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { count } = await supabase
        .from('vouchers')
        .select('id', { count: 'exact', head: true })
        .eq('affiliator_id', session.user.id);

      const isAffiliator = count && count > 0;
      if (isAffiliator) {
        setShowBalance(true);
        CacheService.set('navbar_show_balance', true, 60);
      } else {
        CacheService.set('navbar_show_balance', false, 60);
      }
    };

    checkAffiliateStatus();
  }, [walletBalance]);

  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const creditText = `${(creditBalance || 0).toLocaleString("id-ID")} ${t('common.credit')}`;
  const saldoText = formatRupiah(walletBalance || 0);

  const handleToggleSidebar = () => {
    window.dispatchEvent(new CustomEvent("toggle-dashboard-sidebar"));
  };

  const goTopup = () => navigate("/dashboard/topup");
  const goAffiliator = () => navigate("/dashboard/affiliator");

  return (
    <header
      className="
        h-12 md:h-14
        bg-gradient-to-r from-sky-50 via-white to-indigo-50
        border-b border-slate-200/80
        backdrop-blur-md
        px-3 md:px-4
        flex items-center
        shadow-sm
        relative z-40
      "
    >
      {/* ===== MOBILE/TABLET ( < md ) ===== */}
      <div className="flex w-full items-center justify-between md:hidden">
        {/* kiri: judul + nama user - Smaller Text */}
        <div className="flex flex-col min-w-0">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {pageTitle}
          </span>

          {/* Model Selector (Mobile) */}
          {navbarOptions?.type === 'model_selector' && navbarOptions.models?.length > 0 ? (
            <div className="relative">
              <button
                onClick={() => document.getElementById('mobile-model-dropdown').classList.toggle('hidden')}
                className="flex items-center gap-1 text-sm font-bold text-slate-800 truncate active:text-sky-600"
              >
                {navbarOptions.currentModelName || "Select Model"}
                <ChevronDown size={14} className="text-slate-400" />
              </button>

              {/* Simple Mobile Dropdown (Toggled by ID for simplicity without extra state) */}
              <div id="mobile-model-dropdown" className="hidden absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 z-[60] p-1 animate-in fade-in slide-in-from-top-2">
                <div className="max-h-[300px] overflow-y-auto bg-slate-50/50 rounded-lg p-1">
                  {navbarOptions.models.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        navbarOptions.onSelect(model.id);
                        document.getElementById('mobile-model-dropdown').classList.add('hidden');
                      }}
                      className={`w-full text-left px-3 py-2.5 mb-1 last:mb-0 rounded-lg text-xs font-medium transition-colors flex items-center justify-between
                               ${model.id === navbarOptions.currentModelId
                          ? 'bg-sky-100 text-sky-700 border border-sky-200'
                          : 'bg-white text-slate-600 border border-slate-100'
                        }`}
                    >
                      <span className="font-bold">{model.name}</span>
                      {model.id === navbarOptions.currentModelId && <Sparkles size={12} className="text-sky-600" />}
                    </button>
                  ))}
                </div>
                {/* Backdrop to close */}
                <div
                  className="fixed inset-0 -z-10"
                  onClick={() => document.getElementById('mobile-model-dropdown').classList.add('hidden')}
                />
              </div>
            </div>
          ) : (
            <span className="text-sm font-semibold text-slate-800 truncate">
              {displayName}
            </span>
          )}
        </div>

        {/* kanan: kredit + saldo + burger - Smaller Text */}
        <div className="flex items-center gap-2">


          {/* Mobile Wallet Dropdown */}
          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsMobileWalletOpen(false);
              }
            }}
          >
            <button
              type="button"
              onClick={() => setIsMobileWalletOpen(!isMobileWalletOpen)}
              className="p-1 rounded-lg border border-slate-200 bg-white/80 text-sky-600 hover:bg-slate-50 active:scale-95 shadow-sm transition h-8 w-8 flex items-center justify-center"
            >
              <Wallet size={16} />
            </button>

            {isMobileWalletOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                <div className="p-2 space-y-1">
                  <div className="px-2 py-1.5 bg-slate-50 rounded-lg mb-2">
                    <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                      {i18n.resolvedLanguage === 'en' ? 'My Wallet' : 'Dompet Saya'}
                    </p>
                  </div>

                  <button
                    onClick={goTopup}
                    className="w-full flex items-center justify-between p-2 hover:bg-sky-50 rounded-lg group transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center">
                        <Coins size={14} />
                      </div>
                      <span className="text-xs font-semibold text-slate-600 group-hover:text-sky-700">
                        {t('common.credit')}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-sky-600">{creditBalance?.toLocaleString('id-ID') || 0}</span>
                  </button>

                  {showBalance && (
                    <button
                      onClick={goAffiliator}
                      className="w-full flex items-center justify-between p-2 hover:bg-emerald-50 rounded-lg group transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                          <CreditCard size={14} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 group-hover:text-emerald-700">
                          {i18n.resolvedLanguage === 'en' ? 'Balance' : 'Saldo'}
                        </span>
                      </div>
                      <span className="text-xs font-bold text-emerald-600">{saldoText}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Inspiration Mobile */}
          <a
            href="https://www.pinterest.com/neoai021/?invite_code=c5dd8486256d4ca1ad397080df1c5083&sender=1080582641750971537"
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 rounded-lg border border-slate-200 bg-white/80 text-slate-600 hover:bg-slate-50 active:scale-95 shadow-sm transition h-8 w-8 flex items-center justify-center"
            title="Find Inspiration"
          >
            <Images className="w-4 h-4" />
          </a>

          {/* Language Switcher Mobile Dropdown */}
          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsMobileLangOpen(false);
              }
            }}
          >
            <button
              type="button"
              onClick={() => setIsMobileLangOpen(!isMobileLangOpen)}
              className="px-2 rounded-lg border border-slate-200 bg-white/80 text-slate-600 hover:bg-slate-50 active:scale-95 shadow-sm transition h-8 min-w-[32px] flex items-center justify-center gap-1.5"
            >
              <Globe size={14} />
              <span className="text-[10px] font-bold">{i18n.resolvedLanguage === 'en' ? 'EN' : 'ID'}</span>
            </button>

            {isMobileLangOpen && (
              <div className="absolute top-full right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <button
                  onClick={() => { i18n.changeLanguage('en'); setIsMobileLangOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-[11px] font-medium hover:bg-slate-50 flex items-center justify-between
                  ${i18n.resolvedLanguage === 'en' ? 'text-sky-600 bg-sky-50' : 'text-slate-600'}`}
                >
                  <span>English</span>
                  {i18n.resolvedLanguage === 'en' && <span className="font-bold">✓</span>}
                </button>
                <button
                  onClick={() => { i18n.changeLanguage('id'); setIsMobileLangOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-[11px] font-medium hover:bg-slate-50 flex items-center justify-between
                  ${i18n.resolvedLanguage === 'id' ? 'text-sky-600 bg-sky-50' : 'text-slate-600'}`}
                >
                  <span>Indonesia</span>
                  {i18n.resolvedLanguage === 'id' && <span className="font-bold">✓</span>}
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="
              p-1
              rounded-lg
              border border-slate-200
              bg-white/80
              text-slate-600
              hover:bg-slate-50
              active:scale-95
              shadow-sm
              transition
            "
            onClick={handleToggleSidebar}
            aria-label="Buka menu sidebar"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M4 6h16M4 12h16M4 18h16"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ===== DESKTOP ( >= md ) ===== */}
      <div className="hidden md:flex w-full items-center justify-between">
        {/* kiri: Breadcrumb / Title - Smaller Text */}
        <div className="flex items-center gap-2 text-slate-400 text-[10px] md:text-xs">
          <span className="font-semibold text-slate-700">{pageTitle}</span>

          {/* Model Selector (Desktop) */}
          {navbarOptions?.type === 'model_selector' && navbarOptions.models?.length > 0 && (
            <>
              <span className="text-slate-300">/</span>
              <div className="relative group">
                <button className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors">
                  <span className="font-bold text-sky-600 text-xs truncate max-w-[150px]">
                    {navbarOptions.currentModelName || "Select Model"}
                  </span>
                  <ChevronDown size={12} className="text-slate-400 group-hover:text-slate-600 transition-transform group-focus-within:rotate-180" />
                </button>

                {/* Dropdown Menu */}
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden hidden group-hover:block hover:block z-50 animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-1 max-h-[300px] overflow-y-auto">
                    {navbarOptions.models.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => navbarOptions.onSelect(model.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-between group/item
                             ${model.id === navbarOptions.currentModelId
                            ? 'bg-sky-50 text-sky-700'
                            : 'text-slate-600 hover:bg-slate-50'
                          }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-bold">{model.name}</span>
                          {model.label && <span className="text-[9px] text-slate-400 font-normal">{model.label}</span>}
                        </div>
                        {model.id === navbarOptions.currentModelId && <Sparkles size={12} className="text-sky-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* kanan: User Info + Credits - Smaller/Compact */}
        <div className="flex items-center gap-4">
          {/* Inspiration Link */}


          {/* Credit & Wallet Pills */}
          <div className="flex items-center gap-2">
            <button
              onClick={goTopup}
              className="
                flex items-center gap-1.5
                bg-sky-50 border border-sky-100
                text-sky-700 text-[10px] font-bold
                px-2.5 py-1 rounded-full
                hover:bg-sky-100 transition shadow-sm
              "
              title="Topup Kredit"
            >
              <Coins size={14} className="text-sky-600" strokeWidth={2.5} />
              <span className="translate-y-[0.5px]">{creditText}</span>
            </button>

            {showBalance && (
              <button
                onClick={goAffiliator}
                className="
                  flex items-center gap-1.5
                  bg-emerald-50 border border-emerald-100
                  text-emerald-700 text-[10px] font-bold
                  px-2.5 py-1 rounded-full
                  hover:bg-emerald-100 transition shadow-sm
                "
                title="Cek Saldo Affiliator"
              >
                <CreditCard size={14} className="text-emerald-600" strokeWidth={2.5} />
                <span className="translate-y-[0.5px]">{saldoText}</span>
              </button>
            )}
          </div>

          <div className="h-4 w-px bg-slate-200"></div>

          {/* Inspiration Link */}
          <a
            href="https://www.pinterest.com/neoai021/?invite_code=c5dd8486256d4ca1ad397080df1c5083&sender=1080582641750971537"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:text-sky-600 text-slate-500 font-bold text-[10px] transition-all duration-200 shadow-sm"
          >
            <Images className="w-3 h-3" />
            <span>{t('common.findInspiration')}</span>
          </a>

          {/* Language Switcher Desktop */}
          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsLangMenuOpen(false);
              }
            }}
          >
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:text-sky-600 text-slate-500 font-bold text-[10px] transition-all duration-200 shadow-sm"
            >
              <Globe className="w-3 h-3" />
              <span>{i18n.resolvedLanguage === 'en' ? 'EN' : 'ID'}</span>
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`absolute top-full right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden transform transition-all duration-200 origin-top-right z-50
              ${isLangMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}
            >
              <button
                onClick={() => { i18n.changeLanguage('en'); setIsLangMenuOpen(false); }}
                className={`w-full text-left px-4 py-2 text-[11px] font-medium hover:bg-slate-50 flex items-center justify-between
                ${i18n.resolvedLanguage === 'en' ? 'text-sky-600 bg-sky-50' : 'text-slate-600'}`}
              >
                <span>English</span>
                {i18n.resolvedLanguage === 'en' && <span className="font-bold">✓</span>}
              </button>
              <button
                onClick={() => { i18n.changeLanguage('id'); setIsLangMenuOpen(false); }}
                className={`w-full text-left px-4 py-2 text-[11px] font-medium hover:bg-slate-50 flex items-center justify-between
                ${i18n.resolvedLanguage === 'id' ? 'text-sky-600 bg-sky-50' : 'text-slate-600'}`}
              >
                <span>Indonesia</span>
                {i18n.resolvedLanguage === 'id' && <span className="font-bold">✓</span>}
              </button>
            </div>
          </div>

          <div className="h-4 w-px bg-slate-200"></div>

          {/* User Display */}
          <div className="flex flex-col items-end leading-none">
            <span className="text-xs font-bold text-slate-700">
              {displayName}
            </span>
            <span className="text-[9px] text-slate-400 capitalize mt-0.5">
              {roleLabel || "Member"}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardNavbar;
